import { jwtDecode, JwtPayload } from "jwt-decode";
import { doNotification } from "../../../components/toast/nofitication";

import { handleError } from "../../../helperFunctions";
import { getCookie, setCookie } from "../../../helperFunctions/cookies";
import { CLIENT_COOKIE_KEY } from "../../../utils/constants";
import { BaseAPIResponse } from "../../../utils/types";
import { baseApi } from "../../api";
import { AuthSliceActions } from "./auth.slice";
import {
  DashboardSummary,
  ForgotPasswordChangePayload,
  GetProfileResponse,
  LoginPayload,
  LoginResponse,
  ProfileType,
  RegisterCooperativeAccountPayload,
  RegisterMemberAccountPayload,
  SingleUser,
  VerifyEmailResponse,
} from "./auth.type";

export const authApi = baseApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    verifyEmail: builder.mutation<VerifyEmailResponse, { Email: string }>({
      query: (payload) => {
        return {
          url: `/Account/Status`,
          method: "GET",
          params: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    signIn: builder.mutation<LoginResponse, LoginPayload>({
      query: (payload) => {
        return {
          url: `/token`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const resp = await queryFulfilled;

          if (resp.data.access) {
            setCookie(CLIENT_COOKIE_KEY, resp.data.access);
          }
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    forgotPassword: builder.mutation<BaseAPIResponse, { Email: string }>({
      query: (payload) => {
        return {
          url: `/Account/Password/forgot`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const resp = await queryFulfilled;
          if (resp.data.success) {
            doNotification(
              `Verification code sent to ${arg?.Email}`,
              "success"
            );
          }
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    forgotPasswordChange: builder.mutation<
      BaseAPIResponse,
      ForgotPasswordChangePayload
    >({
      query: (payload) => {
        return {
          url: `/Account/Password/Change`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const resp = await queryFulfilled;
          if (resp.data.success) {
            doNotification(`Password changed successfully`, "success");
          }
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    registerMemberAccount: builder.mutation<
      BaseAPIResponse,
      RegisterMemberAccountPayload
    >({
      query: (payload) => {
        return {
          url: `/Account/Register`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    registerCooperativeAccount: builder.mutation<
      BaseAPIResponse,
      RegisterCooperativeAccountPayload
    >({
      query: (payload) => {
        return {
          url: `/Account/Register/Cooperative`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    resendVerificationCode: builder.mutation<
      BaseAPIResponse,
      { Email: string }
    >({
      query: (payload) => {
        return {
          url: `/Account/Register/resend/code`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const resp = await queryFulfilled;
          if (resp?.data?.success) {
            doNotification(
              `Verification code sent to ${arg?.Email}`,
              "success"
            );
          }
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    verifyAccount: builder.mutation<
      BaseAPIResponse,
      { Email: string; Code: string }
    >({
      query: (payload) => {
        return {
          url: `/Account/Register/verify`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const resp = await queryFulfilled;
          if (resp?.data?.success) {
            doNotification(`Email verified successfully`, "success");
          }
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    getUserProfile: builder.query<GetProfileResponse, void>({
      query: (payload) => {
        return {
          url: `/Account/myprofile`,
          method: "GET",
          body: payload,
        };
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const resp = await queryFulfilled;

          if (resp.data.success) {
            const token = getCookie(CLIENT_COOKIE_KEY);

            const decoded: JwtPayload & { account: ProfileType } =
              jwtDecode(token);

            dispatch(
              AuthSliceActions.profile_updated({
                ...(resp.data.data as SingleUser),
                type: decoded?.account,
              })
            );
          }
        } catch (err) {
          handleError(err, false);
        }
      },
    }),

    updateUserProfile: builder.mutation<BaseAPIResponse, FormData>({
      query: (payload) => {
        return {
          url: `/Account/myprofile`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const resp = await queryFulfilled;
          if (resp?.data?.success) {
            doNotification(`Profile updated successfully`, "success");
          }
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    changePassword: builder.mutation<
      BaseAPIResponse,
      {
        oldpassword: string;
        password: string;
      }
    >({
      query: (payload) => {
        return {
          url: `/Account/Password/Change/new`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const resp = await queryFulfilled;
          if (resp?.data?.success) {
            doNotification(`Password changed successfully`, "success");
          }
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    getUserDashboardSummary: builder.query<DashboardSummary, void>({
      query: (payload) => {
        return {
          url: `/Payment/My/Finances`,
          method: "GET",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, false);
        }
      },
    }),

    getVerificationToken: builder.mutation<DashboardSummary, void>({
      query: () => {
        return {
          url: `/Settings/send/token`,
          method: "GET",
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, false);
        }
      },
    }),

    validateBVN: builder.mutation<
      BaseAPIResponse,
      {
        bvn: string;
        first_name: string;
        last_name: string;
        date_of_birth: string;
        code: string;
      }
    >({
      query: (payload) => {
        return {
          url: `/Account/Check/Validate/BVN`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, false);
        }
      },
    }),

    sendBvnOtp: builder.mutation<
      BaseAPIResponse & {
        data: {
          email: string;
          phone: string;
        };
      },
      {
        bvn: string;
      }
    >({
      query: (payload) => {
        return {
          url: `/Account/Send/OTP/BVN`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    verifyBvnOtp: builder.mutation<
      BaseAPIResponse,
      {
        bvn: string;
        code: string;
      }
    >({
      query: (payload) => {
        return {
          url: `/Account/Check/Validate/BVN/OTP`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, true);
        }
      },
    }),
  }),
});

export const {
  useSignInMutation,
  useVerifyEmailMutation,
  useForgotPasswordMutation,
  useForgotPasswordChangeMutation,
  useRegisterMemberAccountMutation,
  useRegisterCooperativeAccountMutation,
  useResendVerificationCodeMutation,
  useVerifyAccountMutation,
  useGetUserProfileQuery,
  useLazyGetUserProfileQuery,
  useUpdateUserProfileMutation,
  useChangePasswordMutation,
  useGetUserDashboardSummaryQuery,
  useGetVerificationTokenMutation,
  useValidateBVNMutation,
  useSendBvnOtpMutation,
  useVerifyBvnOtpMutation,
} = authApi;
